import {isNotNil} from 'ramda';

const EMPTY_PARAGRAPH = '<p><br></p>';
const SPACED_BLOCKS: ReadonlySet<string> = new Set(['P', 'DIV', 'TABLE', 'BLOCKQUOTE', 'UL', 'OL']);
/** A list follows the sentence that introduces it ("We offer:"), so no gap opens before one. */
const LISTS: ReadonlySet<string> = new Set(['UL', 'OL']);

const isEmptyParagraph = (element: Element) =>
  element.tagName === 'P' &&
  (element.textContent ?? '').trim() === '' &&
  element.querySelector('img') === null;

/**
 * The editor shows paragraphs without a gap, so the employee separates them with an empty line.
 * The model returns one `<p>` per paragraph, which reads as one block: greeting, body and closing
 * run together. An empty paragraph goes between two paragraphs that have none between them.
 * A fragment with loose text at the top level is returned unchanged.
 */
export const spaceProposedParagraphs = (html: string): string => {
  const body = new DOMParser().parseFromString(html, 'text/html').body;
  const hasLooseText = Array.from(body.childNodes).some(
    (node) => node.nodeType === Node.TEXT_NODE && (node.textContent ?? '').trim() !== ''
  );
  const blocks = Array.from(body.children);

  if (hasLooseText || blocks.some((block) => !SPACED_BLOCKS.has(block.tagName))) {
    return html;
  }

  return blocks
    .map((block, index) => {
      const previous = index > 0 ? blocks.at(index - 1) : null;
      const needsGap =
        isNotNil(previous) &&
        !isEmptyParagraph(previous) &&
        !isEmptyParagraph(block) &&
        !LISTS.has(block.tagName);

      return needsGap ? `${EMPTY_PARAGRAPH}${block.outerHTML}` : block.outerHTML;
    })
    .join('');
};
